/**
 * What a vacancy asks for, what a candidate answered, and the difference —
 * which is three-valued, not two.
 *
 * ── The bug this exists to stop ──────────────────────────────────────
 *
 * `ApplicantsPanel` printed a red "falta: Excel" for every requirement the
 * candidate's canonical columns did not contain, and sank that applicant below
 * everyone else. Both are correct when the candidate was asked and said no.
 * Neither is when nobody ever asked.
 *
 * And nobody had. The questionnaire's `skills` question shipped with
 * drizzle/0037, long after the 308 candidates already in the database filled the
 * form; **not one of them has an answer to it**, while 108 of 108 published
 * vacancies name required skills. So the panel was telling recruiters that every
 * applicant to every vacancy lacked every skill it required — in red, with the
 * ranking gate firing on all of them at once.
 *
 * The fix is not to guess the answer. It is to say "no consta" where the truth
 * is that the question was never put, and to keep the hard verdict for the case
 * it was written for: asked, and does not have it.
 *
 * ── What counts as answered ──────────────────────────────────────────
 *
 * The *presence of the key* in the questionnaire, not its contents. An empty
 * list is a real answer — "I hold none" — and a requirement missed against it is
 * a genuine miss. An absent key is silence, and silence is not a no.
 */

export interface VacancyRequirements {
    /** Certifications the posting states as required, canonical ids. */
    requiredCertifications: string[];
    /** Skills the posting states as required, canonical ids. */
    requiredSkills: string[];
    /** Desirable certifications, canonical ids. */
    desiredCertifications: string[];
    /** Desirable skills, canonical ids. */
    desiredSkills: string[];
}

function ids(value: unknown): string[] {
    return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

/**
 * The four canonical lists off a vacancy row.
 *
 * Kept apart rather than pooled, which is the change: pooling them lost the one
 * fact that decides how a gap is described — whether the id came from the
 * question this candidate answered or from the one they never saw.
 */
export function vacancyRequirements(job: Record<string, unknown> | null | undefined): VacancyRequirements {
    const values = job ?? {};
    const requiredCertifications = ids(values.required_certifications_canon);
    const requiredSkills = ids(values.required_skills_canon);
    const required = new Set([...requiredCertifications, ...requiredSkills]);
    return {
        requiredCertifications,
        requiredSkills,
        // A credential that is both required and desirable is required. Listing
        // it twice would show the same id as a hard gate and a bonus at once.
        desiredCertifications: ids(values.desired_certifications_canon).filter(id => !required.has(id)),
        desiredSkills: ids(values.desired_skills_canon).filter(id => !required.has(id))
    };
}

export interface CandidateCredentials {
    /** Every canonical id the candidate claims themselves, from both columns. */
    holds: Set<string>;
    /**
     * Ids we worked out from what they wrote elsewhere — `skills_inferred`,
     * filled by `scripts/infer-talent-skills.ts`, see drizzle/0041.
     *
     * A separate set, not merged into `holds`, because the two answer different
     * questions. "Can this person do X" reads both; "did they say so" reads only
     * the first, and a screen that cannot tell them apart will eventually
     * present our reading of somebody's CV as their own claim.
     */
    inferred: Set<string>;
    /** Did they ever answer the certifications question? */
    answeredCertifications: boolean;
    /** Did they ever answer the skills question? */
    answeredSkills: boolean;
}

function answered(questionnaire: unknown, key: string): boolean {
    if (!questionnaire || typeof questionnaire !== "object" || Array.isArray(questionnaire)) return false;
    return Array.isArray((questionnaire as Record<string, unknown>)[key]);
}

/** What one candidate row says about credentials, and about what it was asked. */
export function candidateCredentials(talent: Record<string, unknown> | null | undefined): CandidateCredentials {
    const values = talent ?? {};
    const holds = new Set<string>([
        ...ids(values.certifications_canon),
        ...ids(values.skills_canon)
    ]);
    return {
        holds,
        // Anything they declared outranks our reading of it: the inference pass
        // already drops those, and this keeps it true for a row written before
        // it did.
        inferred: new Set(ids(values.skills_inferred).filter(id => !holds.has(id))),
        answeredCertifications: answered(values.questionnaire, "certifications"),
        answeredSkills: answered(values.questionnaire, "skills")
    };
}

export interface RequirementVerdict {
    /** The candidate claims it. */
    held: string[];
    /**
     * We read it out of what they wrote elsewhere. Counts as having it —
     * otherwise the inference is pointless — but is labelled as ours wherever
     * it is shown, and never renders as the person's own claim.
     */
    inferred: string[];
    /** They were asked and it is not there. The only disqualifying case. */
    missing: string[];
    /** The question was never put to them. Not evidence either way. */
    unknown: string[];
}

/**
 * Split one vacancy's requirements against one candidate.
 *
 * `unknown` is deliberately not folded into `missing`, and not hidden either.
 * Hiding it would let a vacancy's requirement disappear from the screen because
 * the candidate happened to predate the question — the recruiter would see a
 * clean profile and no hint that anything was unanswered.
 */
export function classifyRequirements(
    requirements: { certifications: string[]; skills: string[] },
    candidate: CandidateCredentials
): RequirementVerdict {
    const held: string[] = [];
    const inferred: string[] = [];
    const missing: string[] = [];
    const unknown: string[] = [];

    const sort = (id: string, wasAsked: boolean) => {
        // Declared first: an id in both sets is theirs, not ours.
        if (candidate.holds.has(id)) held.push(id);
        // An inference answers the requirement whether or not the question was
        // ever put — which is the point of having made it.
        else if (candidate.inferred.has(id)) inferred.push(id);
        else if (wasAsked) missing.push(id);
        else unknown.push(id);
    };

    for (const id of requirements.certifications) sort(id, candidate.answeredCertifications);
    for (const id of requirements.skills) {
        // An id can be required as both a certification and a skill on the same
        // vacancy; it is one requirement and it is already placed.
        if (requirements.certifications.includes(id)) continue;
        sort(id, candidate.answeredSkills);
    }

    return { held, inferred, missing, unknown };
}
